/**
 * Fetches transfer/collaborate target agents and teams from an endpoint every
 * agent can reach (GET /conversations/assignable-agents, gated by
 * CONVERSATIONS_VIEW) — unlike /users/dropdown, which needs ADMIN_USERS_VIEW
 * that agents lack.
 */
import { useEffect, useState } from 'react'
import { api } from '../../../api/client.js'

export interface AssignableAgent {
  agentId: string
  label: string
  teamIds: string[]
  /** Open conversations this agent currently holds. */
  load: number
  /** Their conversation cap; 0 means no limit is configured. */
  maxLoad: number
  /** True when `maxLoad` is set and already reached — show it, don't hide them. */
  atCapacity: boolean
  autoAssignEnabled: boolean
}

/** "8/10", or just "8" when the agent has no configured limit. */
export function formatLoad(a: Pick<AssignableAgent, 'load' | 'maxLoad'>): string {
  return a.maxLoad > 0 ? `${a.load}/${a.maxLoad}` : String(a.load)
}

export interface AssignableTeam {
  id: string
  name: string
  campaignId: string | null
}

export function useAssignableAgents(): {
  agents: AssignableAgent[]
  teams: AssignableTeam[]
  loading: boolean
} {
  const [agents, setAgents] = useState<AssignableAgent[]>([])
  const [teams, setTeams] = useState<AssignableTeam[]>([])
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    api
      .get<{ agents: AssignableAgent[]; teams: AssignableTeam[] }>('/conversations/assignable-agents')
      .then((r) => {
        if (!cancelled) {
          setAgents(r.agents ?? [])
          setTeams(r.teams ?? [])
        }
      })
      .catch((err) => {
        console.error('[useAssignableAgents] fetch error:', err)
        if (!cancelled) {
          setAgents([])
          setTeams([])
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [])

  return { agents, teams, loading }
}
